/**
 * Sticky banner showing the current document-level review state.
 *
 * Variants:
 *   - pending_review: reviewer sees Approve / Request Changes + overall notes
 *     textarea; owner sees a "cancel review" button; everyone sees status.
 *   - changes_requested: shows last reviewer + notes preview, prompting writers
 *     to address feedback. Owner can re-submit (handled by the outline CTA).
 *   - finalized: shows who approved and when (round + reviewer).
 *
 * Returns null for any other artifact status.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  MessageSquareText,
  ShieldCheck,
  XCircle,
  Hourglass,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ReviewDecisionDialog } from '@/components/ReviewDecisionDialog'
import { useArtifactReviewsStore } from '@/stores/artifact-reviews-store'
import { useResponsesStore } from '@/stores/responses-store'
import type { BidArtifact, ArtifactReview, ArtifactReviewDecision } from '@/lib/api'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

export interface ReviewStatusBannerProps {
  artifact: BidArtifact
  currentUserId: string | undefined
  isBidOwner: boolean
}

// Stable empty default so the Zustand selector returns the same reference
// when no reviews are cached yet, avoiding infinite re-renders.
const EMPTY_REVIEWS: ArtifactReview[] = []

export function ReviewStatusBanner({ artifact, currentUserId, isBidOwner }: ReviewStatusBannerProps) {
  const reviews = useArtifactReviewsStore((s) => s.byArtifactId[artifact.id] ?? EMPTY_REVIEWS)
  const load = useArtifactReviewsStore((s) => s.load)
  const cancel = useArtifactReviewsStore((s) => s.cancel)
  const deciding = useArtifactReviewsStore((s) => s.deciding[artifact.id] ?? false)
  const setArtifact = useResponsesStore((s) => s.upsertArtifact)

  useEffect(() => {
    void load(artifact.id)
  }, [artifact.id, load])

  const pendingReview = useMemo(
    () => reviews.find((r) => r.status === 'pending') ?? null,
    [reviews],
  )
  const lastCompleted = useMemo(
    () => reviews.find((r) => r.status === 'completed') ?? null,
    [reviews],
  )

  const [confirmDecision, setConfirmDecision] = useState<ArtifactReviewDecision | null>(null)

  const handleCancel = async () => {
    try {
      const res = await cancel(artifact.id)
      setArtifact(res.artifact)
      toast.success('Review cancelled')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel review'
      toast.error(msg)
    }
  }

  if (artifact.status === 'pending_review' && pendingReview) {
    const isReviewer = pendingReview.reviewerId === currentUserId
    const reviewerLabel = pendingReview.reviewerFullName || pendingReview.reviewerEmail || 'Reviewer'

    return (
      <>
        <BannerShell tint="warning" icon={<Hourglass className="h-4 w-4" />}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">In review — Round {pendingReview.round}</span>
              <Badge variant="outline" className="text-xs font-normal">
                {isReviewer ? 'You are the reviewer' : `Reviewer: ${reviewerLabel}`}
              </Badge>
            </div>
            <p className="text-meta mt-0.5">
              Editing is frozen until {isReviewer ? 'you' : reviewerLabel} {isReviewer ? 'decide' : 'decides'}.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isReviewer && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  className="hover:text-destructive"
                  disabled={deciding}
                  onClick={() => setConfirmDecision('request_changes')}
                >
                  <MessageSquareText className="h-4 w-4 mr-1.5" />
                  Request changes
                </Button>
                <Button
                  size="sm"
                  disabled={deciding}
                  onClick={() => setConfirmDecision('approve')}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  Approve
                </Button>
              </>
            )}
            {!isReviewer && isBidOwner && (
              <Button
                size="sm"
                variant="ghost"
                disabled={deciding}
                onClick={() => void handleCancel()}
              >
                Cancel review
              </Button>
            )}
          </div>
        </BannerShell>

        <ReviewDecisionDialog
          artifact={artifact}
          review={pendingReview}
          decision={confirmDecision}
          onClose={() => setConfirmDecision(null)}
        />
      </>
    )
  }

  if (artifact.status === 'changes_requested' && lastCompleted) {
    const reviewerLabel =
      lastCompleted.reviewerFullName || lastCompleted.reviewerEmail || 'Reviewer'
    return (
      <BannerShell tint="warning" icon={<XCircle className="h-4 w-4" />}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">Changes requested — Round {lastCompleted.round}</span>
            <Badge variant="outline" className="text-xs font-normal">{reviewerLabel}</Badge>
          </div>
          {lastCompleted.overallNotes && (
            <p className="text-meta mt-1 whitespace-pre-wrap line-clamp-3">
              {lastCompleted.overallNotes}
            </p>
          )}
        </div>
      </BannerShell>
    )
  }

  if (artifact.status === 'finalized' && lastCompleted && lastCompleted.decision === 'approve') {
    const reviewerLabel =
      lastCompleted.reviewerFullName || lastCompleted.reviewerEmail || 'Reviewer'
    const decidedDate = lastCompleted.decidedAt
      ? new Date(lastCompleted.decidedAt).toLocaleDateString()
      : null
    return (
      <BannerShell tint="success" icon={<ShieldCheck className="h-4 w-4" />}>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">Approved by {reviewerLabel}</span>
            <Badge variant="outline" className="text-xs font-normal">Round {lastCompleted.round}</Badge>
            {decidedDate && (
              <span className="text-meta">on {decidedDate}</span>
            )}
          </div>
          {lastCompleted.overallNotes && (
            <p className="text-meta mt-1 whitespace-pre-wrap line-clamp-2">
              {lastCompleted.overallNotes}
            </p>
          )}
        </div>
      </BannerShell>
    )
  }

  return null
}

// Use a constant suppression for the unused review type import
export type { ArtifactReview }

interface BannerShellProps {
  tint: 'warning' | 'success'
  icon: React.ReactNode
  children: React.ReactNode
}

function BannerShell({ tint, icon, children }: BannerShellProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 rounded-xl border text-sm',
        tint === 'warning' &&
          'border-[hsl(var(--status-warning)/0.35)] bg-[hsl(var(--status-warning)/0.08)]',
        tint === 'success' &&
          'border-[hsl(var(--status-success)/0.35)] bg-[hsl(var(--status-success)/0.08)]',
      )}
    >
      <span
        className={cn(
          'mt-0.5 shrink-0',
          tint === 'warning' && 'text-[hsl(var(--status-warning))]',
          tint === 'success' && 'text-[hsl(var(--status-success))]',
        )}
      >
        {icon}
      </span>
      {children}
    </div>
  )
}
